/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { createJob } from '../fn/distribution/create-job';
import { CreateJob$Params } from '../fn/distribution/create-job';
import { createRule } from '../fn/distribution/create-rule';
import { CreateRule$Params } from '../fn/distribution/create-rule';
import { deleteJob } from '../fn/distribution/delete-job';
import { DeleteJob$Params } from '../fn/distribution/delete-job';
import { DistributionJobResponse } from '../models/distribution-job-response';
import { DistributionRule } from '../models/distribution-rule';
import { DistributionRuleSummary } from '../models/distribution-rule-summary';
import { getJob } from '../fn/distribution/get-job';
import { GetJob$Params } from '../fn/distribution/get-job';
import { getJobsForRule } from '../fn/distribution/get-jobs-for-rule';
import { GetJobsForRule$Params } from '../fn/distribution/get-jobs-for-rule';
import { getRule } from '../fn/distribution/get-rule';
import { GetRule$Params } from '../fn/distribution/get-rule';
import { getRuleDistributions } from '../fn/distribution/get-rule-distributions';
import { GetRuleDistributions$Params } from '../fn/distribution/get-rule-distributions';
import { getRules } from '../fn/distribution/get-rules';
import { GetRules$Params } from '../fn/distribution/get-rules';
import { getRuleSummary } from '../fn/distribution/get-rule-summary';
import { GetRuleSummary$Params } from '../fn/distribution/get-rule-summary';
import { getUserDistributions } from '../fn/distribution/get-user-distributions';
import { GetUserDistributions$Params } from '../fn/distribution/get-user-distributions';
import { PagedResourcesDistributionRule } from '../models/paged-resources-distribution-rule';
import { PagedResourcesUserDistribution } from '../models/paged-resources-user-distribution';
import { updateJob } from '../fn/distribution/update-job';
import { UpdateJob$Params } from '../fn/distribution/update-job';
import { updateRule } from '../fn/distribution/update-rule';
import { UpdateRule$Params } from '../fn/distribution/update-rule';

@Injectable({ providedIn: 'root' })
export class DistributionService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getRules()` */
  static readonly GetRulesPath = '/distribution-rules';

  /**
   * get all rules
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRules()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRules$Response(params: GetRules$Params, context?: HttpContext): Observable<StrictHttpResponse<PagedResourcesDistributionRule>> {
    return getRules(this.http, this.rootUrl, params, context);
  }

  /**
   * get all rules
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRules$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRules(params: GetRules$Params, context?: HttpContext): Observable<PagedResourcesDistributionRule> {
    return this.getRules$Response(params, context).pipe(
      map((r: StrictHttpResponse<PagedResourcesDistributionRule>): PagedResourcesDistributionRule => r.body)
    );
  }

  /** Path part for operation `createRule()` */
  static readonly CreateRulePath = '/distribution-rules';

  /**
   * create a new rule
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createRule()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createRule$Response(params: CreateRule$Params, context?: HttpContext): Observable<StrictHttpResponse<DistributionRule>> {
    return createRule(this.http, this.rootUrl, params, context);
  }

  /**
   * create a new rule
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createRule$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createRule(params: CreateRule$Params, context?: HttpContext): Observable<DistributionRule> {
    return this.createRule$Response(params, context).pipe(
      map((r: StrictHttpResponse<DistributionRule>): DistributionRule => r.body)
    );
  }

  /** Path part for operation `getRule()` */
  static readonly GetRulePath = '/distribution-rules/{ruleId}';

  /**
   * get a rule
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRule()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRule$Response(params: GetRule$Params, context?: HttpContext): Observable<StrictHttpResponse<DistributionRule>> {
    return getRule(this.http, this.rootUrl, params, context);
  }

  /**
   * get a rule
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRule$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRule(params: GetRule$Params, context?: HttpContext): Observable<DistributionRule> {
    return this.getRule$Response(params, context).pipe(
      map((r: StrictHttpResponse<DistributionRule>): DistributionRule => r.body)
    );
  }

  /** Path part for operation `updateRule()` */
  static readonly UpdateRulePath = '/distribution-rules/{ruleId}';

  /**
   * update a rule
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRule()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRule$Response(params: UpdateRule$Params, context?: HttpContext): Observable<StrictHttpResponse<DistributionRule>> {
    return updateRule(this.http, this.rootUrl, params, context);
  }

  /**
   * update a rule
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRule$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRule(params: UpdateRule$Params, context?: HttpContext): Observable<DistributionRule> {
    return this.updateRule$Response(params, context).pipe(
      map((r: StrictHttpResponse<DistributionRule>): DistributionRule => r.body)
    );
  }

  /** Path part for operation `getJobsForRule()` */
  static readonly GetJobsForRulePath = '/distribution-rules/{ruleId}/jobs';

  /**
   * get jobs for a rule
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getJobsForRule()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJobsForRule$Response(params: GetJobsForRule$Params, context?: HttpContext): Observable<StrictHttpResponse<Array<DistributionJobResponse>>> {
    return getJobsForRule(this.http, this.rootUrl, params, context);
  }

  /**
   * get jobs for a rule
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getJobsForRule$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJobsForRule(params: GetJobsForRule$Params, context?: HttpContext): Observable<Array<DistributionJobResponse>> {
    return this.getJobsForRule$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<DistributionJobResponse>>): Array<DistributionJobResponse> => r.body)
    );
  }

  /** Path part for operation `createJob()` */
  static readonly CreateJobPath = '/distribution-rules/{ruleId}/jobs';

  /**
   * create a job
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createJob()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createJob$Response(params: CreateJob$Params, context?: HttpContext): Observable<StrictHttpResponse<DistributionJobResponse>> {
    return createJob(this.http, this.rootUrl, params, context);
  }

  /**
   * create a job
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createJob$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createJob(params: CreateJob$Params, context?: HttpContext): Observable<DistributionJobResponse> {
    return this.createJob$Response(params, context).pipe(
      map((r: StrictHttpResponse<DistributionJobResponse>): DistributionJobResponse => r.body)
    );
  }

  /** Path part for operation `getJob()` */
  static readonly GetJobPath = '/distribution-jobs/{jobId}';

  /**
   * get a job
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getJob()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJob$Response(params: GetJob$Params, context?: HttpContext): Observable<StrictHttpResponse<DistributionJobResponse>> {
    return getJob(this.http, this.rootUrl, params, context);
  }

  /**
   * get a job
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getJob$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJob(params: GetJob$Params, context?: HttpContext): Observable<DistributionJobResponse> {
    return this.getJob$Response(params, context).pipe(
      map((r: StrictHttpResponse<DistributionJobResponse>): DistributionJobResponse => r.body)
    );
  }

  /** Path part for operation `updateJob()` */
  static readonly UpdateJobPath = '/distribution-jobs/{jobId}';

  /**
   * update a job
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateJob()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateJob$Response(params: UpdateJob$Params, context?: HttpContext): Observable<StrictHttpResponse<DistributionJobResponse>> {
    return updateJob(this.http, this.rootUrl, params, context);
  }

  /**
   * update a job
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateJob$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateJob(params: UpdateJob$Params, context?: HttpContext): Observable<DistributionJobResponse> {
    return this.updateJob$Response(params, context).pipe(
      map((r: StrictHttpResponse<DistributionJobResponse>): DistributionJobResponse => r.body)
    );
  }

  /** Path part for operation `deleteJob()` */
  static readonly DeleteJobPath = '/distribution-jobs/{jobId}';

  /**
   * delete a job
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteJob()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteJob$Response(params: DeleteJob$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deleteJob(this.http, this.rootUrl, params, context);
  }

  /**
   * delete a job
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteJob$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteJob(params: DeleteJob$Params, context?: HttpContext): Observable<void> {
    return this.deleteJob$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `getRuleSummary()` */
  static readonly GetRuleSummaryPath = '/distribution-rules/{ruleId}/summary';

  /**
   * get a rule summary
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRuleSummary()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRuleSummary$Response(params: GetRuleSummary$Params, context?: HttpContext): Observable<StrictHttpResponse<DistributionRuleSummary>> {
    return getRuleSummary(this.http, this.rootUrl, params, context);
  }

  /**
   * get a rule summary
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRuleSummary$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRuleSummary(params: GetRuleSummary$Params, context?: HttpContext): Observable<DistributionRuleSummary> {
    return this.getRuleSummary$Response(params, context).pipe(
      map((r: StrictHttpResponse<DistributionRuleSummary>): DistributionRuleSummary => r.body)
    );
  }

  /** Path part for operation `getRuleDistributions()` */
  static readonly GetRuleDistributionsPath = '/distribution-rules/{ruleId}/distributions';

  /**
   * get a rule distributions
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRuleDistributions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRuleDistributions$Response(params: GetRuleDistributions$Params, context?: HttpContext): Observable<StrictHttpResponse<PagedResourcesUserDistribution>> {
    return getRuleDistributions(this.http, this.rootUrl, params, context);
  }

  /**
   * get a rule distributions
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRuleDistributions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRuleDistributions(params: GetRuleDistributions$Params, context?: HttpContext): Observable<PagedResourcesUserDistribution> {
    return this.getRuleDistributions$Response(params, context).pipe(
      map((r: StrictHttpResponse<PagedResourcesUserDistribution>): PagedResourcesUserDistribution => r.body)
    );
  }

  /** Path part for operation `getUserDistributions()` */
  static readonly GetUserDistributionsPath = '/users/{userId}/distributions';

  /**
   * get a user distributions
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUserDistributions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserDistributions$Response(params: GetUserDistributions$Params, context?: HttpContext): Observable<StrictHttpResponse<PagedResourcesUserDistribution>> {
    return getUserDistributions(this.http, this.rootUrl, params, context);
  }

  /**
   * get a user distributions
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUserDistributions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserDistributions(params: GetUserDistributions$Params, context?: HttpContext): Observable<PagedResourcesUserDistribution> {
    return this.getUserDistributions$Response(params, context).pipe(
      map((r: StrictHttpResponse<PagedResourcesUserDistribution>): PagedResourcesUserDistribution => r.body)
    );
  }

}
